import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { panelAPI } from "../../services/api";
import { panelConflictOfInterestSchema } from "../../types/schemas";
import {
  FEMA_REGION_OPTIONS,
  NFIP_WORK_LEVEL_OPTIONS,
  PTS_JOINT_VENTURE_OPTIONS,
  US_STATES,
  WORK_LEVEL_OPTIONS,
  YES_NO_ONLY_OPTIONS,
} from "../../utils/constants";
import Dropdown from "../inputs/Dropdown";
import Input from "../inputs/Input";
import Modal from "./Modal";

const PanelConflictOfInterestModal = ({ isOpen, onClose, panelId }) => {
  const navigate = useNavigate();
  const { control, handleSubmit, watch, trigger, reset } = useForm({
    resolver: zodResolver(panelConflictOfInterestSchema),
    defaultValues: {
      conflictOfInterest: {
        employeeOfFemaOrDhs: "",
        retiredEmployeeOfFemaOrDhs: "",
        currentEmployeeOrContractorOfFEMA_PTS: "",
        ptsJointVenture: "",
        workLevel: "",
        regions: [],
        states: [],
        localCommunityName: "",
        pastEmployeeOrContractor: "",
        nfipInvolvement: "",
        nfipWorkLevel: "",
        nfipDetails: "",
      },
    },
    mode: "onChange",
  });

  // Watch specific fields for conditional rendering
  const watchedCurrentEmployee = watch(
    "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
  );
  const watchedWorkLevel = watch("conflictOfInterest.workLevel");
  const watchedNfipInvolvement = watch("conflictOfInterest.nfipInvolvement");

  const yesNoOnlyOptions = YES_NO_ONLY_OPTIONS;
  const ptsJointVentureOptions = PTS_JOINT_VENTURE_OPTIONS;
  const workLevelOptions = WORK_LEVEL_OPTIONS;
  const nfipWorkLevelOptions = NFIP_WORK_LEVEL_OPTIONS;
  const regions = FEMA_REGION_OPTIONS;
  const states = ["None", ...US_STATES];

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const [deadlinePassed, setDeadlinePassed] = useState(false);

  useEffect(() => {
    const loadPanel = async () => {
      if (!panelId || !isOpen) return;
      try {
        const resp = await panelAPI.getPanelById(panelId);
        if (resp?.success && resp?.panel) {
          if (resp.panel.cadreMemberResponseDate) {
            const now = new Date();
            const deadline = new Date(resp.panel.cadreMemberResponseDate);
            setDeadlinePassed(now > deadline);
          } else {
            setDeadlinePassed(false);
          }
        }
      } catch (e) {
        // Non-fatal: keep modal functional; backend will enforce as well
      }
    };
    loadPanel();
  }, [panelId, isOpen]);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      if (deadlinePassed) {
        // Safety guard; backend also enforces
        navigate(-1);
        return;
      }
      // Prepare form data similar to CadreApplication
      const submitData = {
        employeeOfFemaOrDhs: data.conflictOfInterest.employeeOfFemaOrDhs,
        retiredEmployeeOfFemaOrDhs:
          data.conflictOfInterest.retiredEmployeeOfFemaOrDhs,
        currentEmployeeOrContractorOfFEMA_PTS:
          data.conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS,
        pastEmployeeOrContractor:
          data.conflictOfInterest.pastEmployeeOrContractor,
        nfipInvolvement: data.conflictOfInterest.nfipInvolvement,
        ptsJointVenture: data.conflictOfInterest.ptsJointVenture,
        workLevel: data.conflictOfInterest.workLevel,
        regions: JSON.stringify(data.conflictOfInterest.regions || []),
        states: JSON.stringify(data.conflictOfInterest.states || []),
        localCommunityName: data.conflictOfInterest.localCommunityName,
        nfipWorkLevel: data.conflictOfInterest.nfipWorkLevel,
        nfipDetails: data.conflictOfInterest.nfipDetails,
      };

      const response = await panelAPI.submitPanelConflictOfInterest(
        panelId,
        submitData
      );

      if (response.success) {
        const evaluatedStatus = response?.conflictOfInterest?.evaluatedStatus;
        if (evaluatedStatus === "YES") {
          toast.warning(
            "Thank you for completing the conflict of interest. Since a potential conflict was identified, this panel cannot be assigned to you. Please contact the panel administrator if you believe this is an error."
          );
          // Redirect immediately to panels list if conflict identified
          navigate("/panels");
          return;
        } else {
          toast.success("Conflict of Interest submitted successfully!");
          // Close and trigger refresh so panel becomes accessible
          onClose();
        }
      } else {
        throw new Error(response.message || "Submission failed");
      }
    } catch (error) {
      console.error("Error submitting Conflict of Interest:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to submit Conflict of Interest. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleNextStep = async () => {
    try {
      if (submitting) return;
      setSubmitting(true);
      const isValid = await trigger();

      if (isValid) {
        await handleSubmit(onSubmit)();
      } else {
        toast.error("Please fill in all required fields correctly.");
      }
    } catch (error) {
      console.error("Error in handleNextStep:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Redirect back to previous page when X is clicked
    navigate(-1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Conflict of Interest Disclosure"
      size="xl"
      className="max-h-[90vh] overflow-y-auto"
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {deadlinePassed && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
            You can no longer complete the Conflict of Interest form for this
            panel. The response deadline has passed.
          </div>
        )}
        {!deadlinePassed && (
          <div className="border-b border-gray-200 pb-4 mb-4">
            <p className="text-gray-600 text-sm">
              Before accessing this panel, you must complete the Conflict of
              Interest disclosure. Please answer all questions below.
            </p>
          </div>
        )}

        {!deadlinePassed && (
          <>
            {/* Question 1 */}
            <div className="mb-6">
              <Controller
                name="conflictOfInterest.employeeOfFemaOrDhs"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      <span className="font-bold text-blue-600">1.</span> Are
                      you a current or past employee of FEMA or DHS?
                    </p>
                    <div className="space-y-2">
                      {yesNoOnlyOptions.map((option) => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="employeeOfFemaOrDhs"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Question 2 */}
            <div className="mb-6">
              <Controller
                name="conflictOfInterest.retiredEmployeeOfFemaOrDhs"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      <span className="font-bold text-blue-600">2.</span> Are
                      you a retired employee of FEMA or DHS?
                    </p>
                    <div className="space-y-2">
                      {yesNoOnlyOptions.map((option) => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="retiredEmployeeOfFemaOrDhs"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Question 3 */}
            <div className="mb-6">
              <Controller
                name="conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      <span className="font-bold text-blue-600">3.</span>{" "}
                      Current employee or contractor of FEMA/PTS? (Are you a
                      current employee of a firm providing contracting support
                      to FEMA or a firm which is a subcontractor of a Production
                      and Technical Services (PTS) firm?)
                    </p>
                    <div className="space-y-2">
                      {yesNoOnlyOptions.map((option) => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="currentEmployeeOrContractorOfFEMA_PTS"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              {/* Conditional fields for Question 3 */}
              {watchedCurrentEmployee === "YES" && (
                <div className="mt-4 ml-6 space-y-4">
                  <Controller
                    name="conflictOfInterest.ptsJointVenture"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-2">
                          Which PTS joint venture do/did you or your firm work
                          with?
                        </p>
                        <Dropdown
                          options={ptsJointVentureOptions}
                          onSelect={field.onChange}
                          value={field.value}
                          label="Select PTS Joint Venture"
                          required
                          error={fieldState.error}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="conflictOfInterest.workLevel"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-2">
                          Are you, or were you, working at the National,
                          Regional, State or Local Level?
                        </p>
                        <Dropdown
                          options={workLevelOptions}
                          onSelect={field.onChange}
                          value={field.value}
                          label="Select Work Level"
                          required
                          error={fieldState.error}
                        />
                      </div>
                    )}
                  />

                  {/* Regions (only if Regional) */}
                  {watchedWorkLevel === "Regional" && (
                    <Controller
                      name="conflictOfInterest.regions"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Select the regions in which you worked
                          </p>
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-3">
                            {regions.map((region) => (
                              <label key={region} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={field.value.includes(region)}
                                  onChange={(e) => {
                                    const newValues = e.target.checked
                                      ? [...field.value, region]
                                      : field.value.filter((r) => r !== region);
                                    field.onChange(newValues);
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-gray-700">
                                  Region {region}
                                </span>
                              </label>
                            ))}
                          </div>
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  )}

                  {/* States (if Regional OR State OR Local) */}
                  {(watchedWorkLevel === "Regional" ||
                    watchedWorkLevel === "State" ||
                    watchedWorkLevel === "Local") && (
                    <Controller
                      name="conflictOfInterest.states"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Select the states in which you worked
                          </p>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-3">
                            <div className="grid grid-cols-2 gap-2">
                              {states.map((state) => (
                                <label
                                  key={state}
                                  className="flex items-center"
                                >
                                  <input
                                    type="checkbox"
                                    checked={field.value.includes(state)}
                                    onChange={(e) => {
                                      const newValues = e.target.checked
                                        ? [...field.value, state]
                                        : field.value.filter(
                                            (s) => s !== state
                                          );
                                      field.onChange(newValues);
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-gray-700 text-sm">
                                    {state}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  )}

                  {/* Local Community Name */}
                  {watchedWorkLevel === "Local" && (
                    <Controller
                      name="conflictOfInterest.localCommunityName"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <Input
                            label="Enter your community name"
                            field={field}
                            required
                            placeholder="Enter community name"
                            error={fieldState.error}
                          />
                        </div>
                      )}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Question 4 */}
            <div className="mb-6">
              <Controller
                name="conflictOfInterest.pastEmployeeOrContractor"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      <span className="font-bold text-blue-600">4.</span> Past
                      employee or contractor of FEMA/PTS
                    </p>
                    <div className="space-y-2">
                      {yesNoOnlyOptions.map((option) => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="pastEmployeeOrContractor"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Question 5 */}
            <div className="mb-6">
              <Controller
                name="conflictOfInterest.nfipInvolvement"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      <span className="font-bold text-blue-600">5.</span> Are
                      you currently involved, or have you ever been involved, in
                      an NFIP related flood mapping study other than PTS
                      Contracting?
                    </p>
                    <div className="space-y-2">
                      {yesNoOnlyOptions.map((option) => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="nfipInvolvement"
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              {/* NFIP Work Level and Description */}
              {watchedNfipInvolvement === "YES" && (
                <div className="mt-4 ml-6 space-y-4">
                  <Controller
                    name="conflictOfInterest.nfipWorkLevel"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-2">
                          Are you, or were you, working at the Federal, State or
                          Local level?
                        </p>
                        <Dropdown
                          options={nfipWorkLevelOptions}
                          onSelect={field.onChange}
                          value={field.value}
                          label="Select NFIP Work Level"
                          required
                          error={fieldState.error}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="conflictOfInterest.nfipDetails"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-2">
                          Please describe your involvement in a flood study and
                          indicate the name of the local, state or federal
                          agency initiative for which you worked and the date of
                          the study.
                        </p>

                        <textarea
                          {...field}
                          rows={4}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent ${
                            fieldState.error
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="Please describe your involvement ....."
                        />
                        {fieldState.error && (
                          <p className="mt-1 text-sm text-red-600">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
        {deadlinePassed ? (
          <button
            onClick={handleClose}
            className="px-6 py-2 rounded-lg transition-colors font-medium bg-gray-200 text-gray-800 hover:bg-gray-300"
          >
            Close
          </button>
        ) : (
          <button
            onClick={handleNextStep}
            disabled={submitting}
            className={`px-6 py-2 rounded-lg transition-colors font-medium ${
              submitting
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-custom-blue text-white hover:bg-custom-light-blue"
            }`}
          >
            {submitting ? "Submitting..." : "Submit & Continue"}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default PanelConflictOfInterestModal;
